// src/shared/utils/formatAddress.js

/**
 * Format an address from its parts.
 * 
 * @param {Object} options - Address parts
 * @param {string} [options.street_address] - Street address
 * @param {string} [options.city] - City
 * @param {string} [options.fallback] - Fallback text when no address parts are available
 * @returns {string} Formatted address string
 * 
 * @example
 * formatAddress({ street_address: '123 Main St', city: 'Boston' })
 * // => '123 Main St, Boston'
 * 
 * formatAddress({ street_address: '123 Main St' })
 * // => '123 Main St'
 * 
 * formatAddress({ city: 'Boston' })
 * // => 'Boston'
 * 
 * formatAddress({})
 * // => 'No address'
 * 
 * formatAddress({}, { fallback: '—' })
 * // => '—'
 */
export function formatAddress({ street_address, city }, { fallback = 'No address' } = {}) {
  const parts = [street_address, city].filter(Boolean).map(s => s.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : fallback;
}

/**
 * Get address parts as an array (useful for multi-line display).
 * 
 * @param {Object} options - Address parts
 * @param {string} [options.street_address] - Street address
 * @param {string} [options.city] - City
 * @returns {string[]} Array of non-empty address parts
 * 
 * @example
 * getAddressParts({ street_address: '123 Main St', city: 'Boston' })
 * // => ['123 Main St', 'Boston']
 */
export function getAddressParts({ street_address, city }) {
  return [street_address, city].filter(Boolean).map(s => s.trim()).filter(Boolean);
}

/**
 * Check if an address has any parts.
 * 
 * @param {Object} options - Address parts
 * @param {string} [options.street_address] - Street address
 * @param {string} [options.city] - City
 * @returns {boolean} True if at least one address part exists
 */
export function hasAddress({ street_address, city }) {
  return getAddressParts({ street_address, city }).length > 0;
}
